import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Footer from "@/components/Footer";
import {
  Trophy,
  Calendar,
  Gift,
  Gavel,
  AlertTriangle,
  ShieldAlert,
  CircleDot,
  Smartphone,
  Monitor,
  Gamepad2,
  ArrowRight,
} from "lucide-react";

const articles = [
  {
    n: "Art. 1º",
    title: "Profanação & discurso de ódio",
    body: "Não é permitida linguagem obscena, insultos, ameaças, difamação ou qualquer tipo de discriminação. Nomes de utilizador ofensivos também não são autorizados.",
    icon: ShieldAlert,
  },
  {
    n: "Art. 2º",
    title: "Criação de equipas",
    body: "Inscrições com membros já registados noutra equipa serão automaticamente desqualificadas. Falsificação de ranks ou IDs implica penalização.",
    icon: Gavel,
  },
  {
    n: "Art. 3º",
    title: "Batota",
    body: "O uso de softwares que confiram vantagem injusta ou de bugs/exploits é expressamente proibido.",
    icon: AlertTriangle,
  },
  {
    n: "Art. 4º",
    title: "Penalizações",
    body: "A violação dos artigos 1º e 3º resulta em desclassificação imediata da equipa ou jogador.",
    icon: CircleDot,
  },
  {
    n: "Art. 5º",
    title: "Atrasos",
    body: "Não comparecimento à hora marcada implica penalização ao critério da organização.",
    icon: Calendar,
  },
  {
    n: "Art. 6º",
    title: "Decisões da organização",
    body: "Todas as decisões da organização são finais e não admitem recurso.",
    icon: Gavel,
  },
  {
    n: "Art. 7º",
    title: "Permanência nas salas",
    body: "Apenas membros das equipas em jogo e pessoas com autorização da organização podem permanecer nas salas dos torneios.",
    icon: ShieldAlert,
  },
  {
    n: "Art. 8º",
    title: "Aceitação do regulamento",
    body: "A submissão da inscrição implica a aceitação total deste regulamento.",
    icon: Gavel,
  },
  {
    n: "Art. 9º",
    title: "Prémios",
    body: "Os vencedores de cada torneio recebem um vale Norteshopping no valor de 50€ por pessoa.",
    icon: Gift,
  },
];

const modalities = [
  {
    to: "/mobile",
    title: "Mobile",
    subtitle: "Free Fire · COD Mobile",
    icon: Smartphone,
    accent: "#F97316",
    testid: "card-mobile",
    img:
      "https://images.pexels.com/photos/7773745/pexels-photo-7773745.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    to: "/pc",
    title: "PC",
    subtitle: "Fortnite",
    icon: Monitor,
    accent: "#A78BFA",
    testid: "card-pc",
    img:
      "https://images.pexels.com/photos/15870664/pexels-photo-15870664.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    to: "/consola",
    title: "Consola",
    subtitle: "EA SPORTS FC 25",
    icon: Gamepad2,
    accent: "#22D3EE",
    testid: "card-consola",
    img:
      "https://images.unsplash.com/photo-1522778119026-d647f0596c20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjA2MTJ8MHwxfHNlYXJjaHwxfHxzb2NjZXIlMjBzdGFkaXVtJTIwbmlnaHR8ZW58MHx8fHwxNzgxODY3NDc0fDA&ixlib=rb-4.1.0&q=85",
  },
];

export default function HomePage() {
  return (
    <div data-testid="home-page" className="bg-grain">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1542751371-adc38448a05e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxlc3BvcnRzJTIwdG91cm5hbWVudHxlbnwwfHx8fDE3ODE4Njc0NzR8MA&ixlib=rb-4.1.0&q=85)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black" />

        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-24 pb-20 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.35em] text-[#00F0FF] mb-6">
              <span className="h-px w-8 bg-[#00F0FF]" />
              ESTEL · Edição 25/26
            </div>
            <h1 className="font-display text-5xl sm:text-7xl md:text-[6.5rem] uppercase leading-[0.9] tracking-wider">
              Torneio de
              <br />
              <span className="text-[#00F0FF] glow-cyan">Jogos</span> Eletrónicos
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed">
              Três modalidades. Quatro jogos. Uma escola.
              <br className="hidden sm:block" />
              O palco da próxima geração de jogadores ESTEL.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <a
                href="#regulamento"
                data-testid="hero-rules-btn"
                className="inline-flex items-center gap-2 bg-[#00F0FF] text-black px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-white transition-colors"
              >
                Ver regulamento
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="#modalidades"
                data-testid="hero-modalities-btn"
                className="inline-flex items-center gap-2 border border-white/20 px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:border-white hover:bg-white/5 transition-colors"
              >
                Modalidades
              </a>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10"
          >
            {[
              { k: "3", l: "Modalidades" },
              { k: "4", l: "Jogos" },
              { k: "32", l: "Equipas FC25" },
              { k: "50€", l: "Vale prémio" },
            ].map((s) => (
              <div
                key={s.l}
                className="bg-[#0A0A0A] p-5 sm:p-6"
                data-testid={`stat-${s.l.toLowerCase()}`}
              >
                <div className="font-display text-4xl sm:text-5xl text-[#00F0FF]">
                  {s.k}
                </div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Marquee */}
        <div className="border-y border-white/10 bg-black py-3 overflow-hidden">
          <div className="marquee-track">
            {[...Array(2)].map((_, k) => (
              <div key={k} className="flex items-center gap-12 px-6 whitespace-nowrap">
                {[
                  "FREE FIRE",
                  "·",
                  "CALL OF DUTY: MOBILE",
                  "·",
                  "FORTNITE",
                  "·",
                  "EA SPORTS FC 25",
                  "·",
                  "ESTEL 25/26",
                  "·",
                ].map((t, i) => (
                  <span
                    key={`${k}-${i}`}
                    className={`font-display text-2xl uppercase tracking-[0.2em] ${
                      t === "·" ? "text-[#00F0FF]" : "text-white/40"
                    }`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODALIDADES */}
      <section
        id="modalidades"
        className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28"
      >
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">
              03 Modalidades
            </div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl uppercase tracking-wider">
              Escolhe o teu campo
              <br />
              de batalha
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {modalities.map((m, idx) => {
            const Icon = m.icon;
            return (
              <motion.div
                key={m.to}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1, duration: 0.5 }}
              >
                <Link
                  to={m.to}
                  data-testid={m.testid}
                  className="group relative block aspect-[3/4] overflow-hidden border border-white/10 hover:border-white/40 transition-colors"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url(${m.img})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{
                      background: `linear-gradient(to top, ${m.accent}30, transparent 50%)`,
                    }}
                  />

                  <div className="relative h-full p-6 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <Icon
                        className="h-8 w-8"
                        style={{ color: m.accent }}
                        strokeWidth={1.5}
                      />
                      <span
                        className="text-[10px] uppercase tracking-[0.3em] font-semibold"
                        style={{ color: m.accent }}
                      >
                        0{idx + 1}
                      </span>
                    </div>
                    <div>
                      <div className="font-display text-5xl uppercase tracking-wider leading-none mb-2">
                        {m.title}
                      </div>
                      <div className="text-sm text-white/60 mb-5">
                        {m.subtitle}
                      </div>
                      <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-white/80 group-hover:text-white">
                        Explorar
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* REGULAMENTO */}
      <section
        id="regulamento"
        className="border-t border-white/5 py-20 md:py-28"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">
                Regras Oficiais
              </div>
              <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-wider leading-none">
                Regulamento
                <br />
                Geral
              </h2>
              <p className="text-sm text-white/60 mt-5 leading-relaxed">
                A submissão da inscrição implica aceitação total destes
                artigos. Todas as decisões da organização são finais e
                inapeláveis.
              </p>

              <div className="mt-8 grid grid-cols-1 gap-3">
                <div className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-4">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#00F0FF]">
                    <Gift className="h-3.5 w-3.5" />
                    Prémios
                  </div>
                  <div className="mt-2 text-sm text-white">
                    Vale <span className="font-bold">Norteshopping 50€</span>{" "}
                    por pessoa vencedora.
                  </div>
                </div>
                <div className="border border-white/10 p-4">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/60">
                    <Trophy className="h-3.5 w-3.5" />
                    Formato
                  </div>
                  <div className="mt-2 text-sm text-white/80">
                    Eliminatórias diretas. Quem vence avança, quem perde sai.
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-3">
              {articles.map((a, i) => {
                const Icon = a.icon;
                return (
                  <motion.div
                    key={a.n}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.03, duration: 0.4 }}
                    data-testid={`rule-${a.n.replace(/[^a-z0-9]/gi, "")}`}
                    className="border border-white/10 bg-[#141414] p-5 hover:border-[#00F0FF]/40 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] font-semibold">
                        {a.n}
                      </span>
                      <Icon className="h-4 w-4 text-white/40" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-xl uppercase tracking-wider mb-2">
                      {a.title}
                    </h3>
                    <p className="text-sm text-white/60 leading-relaxed">
                      {a.body}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="border-t border-white/5 py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-3">
            Modo de Funcionamento
          </div>
          <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-wider mb-12">
            Como decorre o torneio
          </h2>

          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              {
                n: "01",
                t: "Inscrição & Sorteio",
                d: "Equipas e jogadores inscritos são sorteados nos respetivos quadros.",
              },
              {
                n: "02",
                t: "Eliminatórias",
                d: "Confrontos diretos. Quem vence avança para a fase seguinte; quem perde está eliminado.",
              },
              {
                n: "03",
                t: "Final",
                d: "A grande final decide o vencedor da modalidade. Em FC25, em caso de empate vai-se direto a grandes penalidades.",
              },
            ].map((it) => (
              <div key={it.n} className="bg-[#0A0A0A] p-8">
                <div className="font-display text-6xl text-[#00F0FF]/30 leading-none">
                  {it.n}
                </div>
                <h3 className="font-display text-2xl uppercase tracking-wider mt-3">
                  {it.t}
                </h3>
                <p className="text-sm text-white/60 mt-3 leading-relaxed">
                  {it.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
